import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { list, update } from "@/lib/api";
import { computeConflicts } from "@/lib/conflicts";
import { Layout, PageHeader } from "@/components/Layout";
import { StatusPill, PriorityPill } from "@/components/StatusPill";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, User, Building2, Truck, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = ["Submitted", "Approved", "Scheduled", "In Progress", "Completed"];
const NEXT = { Submitted: "Approved", Approved: "Scheduled", Scheduled: "In Progress", "In Progress": "Completed" };
const fmt = (v) => (v ? String(v).slice(0, 16).replace("T", " ") : null);

export default function Schedule() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["requisitions"], queryFn: () => list("requisitions") });

  const advanceMut = useMutation({
    mutationFn: ({ id, status }) => update("requisitions", id, { status }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["requisitions"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Moved to ${v.status}`);
    },
  });

  const grouped = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c, []]));
    data.forEach((r) => { if (g[r.status]) g[r.status].push(r); });
    return g;
  }, [data]);

  const conflictIds = useMemo(() => computeConflicts(data), [data]);

  return (
    <Layout>
      <PageHeader title="Schedule Board" subtitle="Truck & job scheduling pipeline — advance jobs across stages" />
      <div className="p-8 animate-fade-up">
        {isLoading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            {COLUMNS.map((col) => (
              <div key={col} data-testid={`sched-col-${col}`} className="flex flex-col min-w-0">
                <div className="flex items-center justify-between mb-3 px-1">
                  <h3 className="font-display font-bold text-sm uppercase tracking-wide">{col}</h3>
                  <Badge variant="secondary" className="font-mono-data">{grouped[col].length}</Badge>
                </div>
                <div className="space-y-3">
                  {grouped[col].length === 0 && (
                    <div className="rounded-md border border-dashed border-border/60 py-8 text-center text-xs text-muted-foreground">Empty</div>
                  )}
                  {grouped[col].map((r) => (
                    <Card key={r.id} data-testid={`sched-card-${r.id}`} className={`p-3.5 border-border/60 shadow-none hover:shadow-md transition-shadow bg-white ${conflictIds.has(r.id) ? "ring-1 ring-amber-300" : ""}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono-data text-xs font-semibold text-[#0055FF]">{r.work_order_no || r.requisition_no}</span>
                        <div className="flex items-center gap-1.5">
                          {conflictIds.has(r.id) && (
                            <span data-testid={`conflict-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">
                              <AlertTriangle className="h-3 w-3" /> Conflict
                            </span>
                          )}
                          <PriorityPill value={r.priority} />
                        </div>
                      </div>
                      <div className="text-sm font-medium truncate mb-1.5">{r.project_name || r.load_description || "Untitled job"}</div>
                      <div className="space-y-1 text-xs text-neutral-600">
                        <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-neutral-400" /><span className="truncate">{r.pickup_location || "?"} → {r.delivery_location || "?"}</span></div>
                        {fmt(r.estimated_start) && <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-neutral-400" />{fmt(r.estimated_start)}</div>}
                        <div className="flex items-center gap-1.5">
                          {r.asset_source === "Internal Asset"
                            ? <><Truck className="h-3.5 w-3.5 text-neutral-400" /><span className="truncate">{[r.driver_name, r.truck_unit_number].filter(Boolean).join(" · ") || "Unassigned"}</span></>
                            : <><Building2 className="h-3.5 w-3.5 text-neutral-400" /><span className="truncate">{r.third_party_carrier || "Unassigned"}</span></>}
                        </div>
                      </div>
                      {NEXT[col] && (
                        <button
                          data-testid={`advance-${r.id}`}
                          onClick={() => advanceMut.mutate({ id: r.id, status: NEXT[col] })}
                          className="mt-3 w-full text-xs font-semibold rounded-md border border-border/60 py-1.5 hover:bg-[#0055FF] hover:text-white hover:border-[#0055FF] transition-colors"
                        >
                          Advance → {NEXT[col]}
                        </button>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
